/**
 * AI assistant streaming client. The ONLY client that bypasses the axios
 * shim — axios buffers the whole response, so SSE token streaming needs a
 * raw `fetch` + `ReadableStream`. Mirrors the shim's auth behaviour by
 * hand: `credentials:"include"` (HttpOnly accessToken cookie), CSRF
 * double-submit header, and a single 401 retry through the shared
 * `refreshAccessTokenOutcome` — the tri-state one, because "unauthorized"
 * makes the provider log the tab out and a reissue that got NO answer must
 * not.
 */
import {
  KPI_METRICS,
  type CohortTarget,
  type EventGoalKpi,
  type KpiMetric,
} from "@/types/EventGoal";
import { coerceCohortTarget, coerceKpi } from "@/lib/goals/aiCoerce";
import { sameKpiIdentity } from "@/lib/goals/kpiIdentity";
import {
  MAX_COHORT_TARGETS,
  MAX_GOAL_TEXT_LENGTH,
  MAX_KPIS,
} from "@/lib/api/handlers/EventGoalHandling";
import { api, readCookie, refreshAccessTokenOutcome } from "../client";
import { rethrowApi } from "../_apiShim";
import { useConnectionStore } from "@/lib/connection/connectionStore";

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";
const CSRF_COOKIE = "csrfToken";

export type AiStreamEvent = {
  type:
    | "conversation"
    | "text_delta"
    | "error"
    | "ping"
    | "tool_use"
    | "tool_result"
    | "tool_confirmation_required"
    | "client_tool"
    | "generation_preview"
    | "generation_progress"
    | "background_job"
    | "credits_charged"
    | "message_stop";
  text?: string;
  conversationId?: string;
  errorCode?: string;
  toolUseId?: string;
  toolName?: string;
  /** Pre-serialized tool input — parse on use (kept opaque on the wire). */
  toolInputJson?: string;
  /** generation_preview only (P2b) — id of the server-held candidate. */
  candidateId?: string;
  /** generation_preview only (P2b) — sanitized primary-locale HTML. */
  previewHtml?: string;
  /** generation_preview only — candidate kind ("document"/"email"/"website"). */
  kind?: string;
  /** generation_progress only (P7) — 1-based page being generated. */
  page?: number;
  /** generation_progress only (P7) — total pages of this generation. */
  total?: number;
  /** generation_progress only (P7) — the page's nav title. */
  title?: string;
  /** background_job only — id of a tracked job a SERVER tool submitted
   *  (guest export / bulk email send); the FE registers it with the
   *  Downloads/progress tracker. */
  backgroundJobId?: string;
  /** background_job only — TrackedJobTaskType (e.g. EXPORT_GUESTS_TO_EXCEL,
   *  SEND_EMAILS); drives tracker scope + display category. */
  taskType?: string;
  /** background_job only — the event the job belongs to (hex), authoritative
   *  from the backend so the FE need not infer it from the live chat scope. */
  eventId?: string;
  /**
   * credits_charged only — AI credits the user's MESSAGE has cost so far,
   * across every HTTP request that message took (a paused turn resumes over a
   * second POST and the count continues). Not the delta of the step that
   * triggered the frame: cumulative on purpose, so a client that misses a
   * frame still lands on the right number instead of summing partial deltas
   * into a wrong one.
   */
  creditCost?: number;
};

export type AiChatBody = {
  conversationId?: string;
  message: string;
  locale?: string;
  /** P5: "studio:website|email|document" when chatting from the AI studio. */
  surface?: string;
  /**
   * P7: the candidate currently shown in the studio's preview pane — adds
   * one system line steering change requests into the iterate lane
   * (baseCandidateId). Inert without a studio surface.
   */
  paneCandidateId?: string;
  /** P6: inspiration-image ids for the generation-model call (max 2). */
  inspoImageIds?: string[];
  /**
   * Image attachments the user added to THIS message — base64 blocks the
   * backend turns into Anthropic image content blocks for the (multimodal)
   * model. Present only when the user actually attached images, so a plain
   * text turn sends a byte-identical body (no regression). Max 4.
   */
  images?: { mediaType: string; data: string }[];
  /**
   * Wizard→event handoff: composed brief from the create-event wizard,
   * sent ONLY on the FIRST message of a fresh event-scoped conversation
   * (no conversationId). The backend pins it as the conversation's brief
   * and injects it into the system prompt every turn. Max 4000 chars.
   */
  handoffBrief?: string;
};

/**
 * Re-entry body for a paused tool call (one endpoint serves both pause
 * flavors — plan §4.5.2): `approved` answers a confirmation gate,
 * `resultJson`/`errorCode` answer a client tool.
 */
export type AiToolResultBody = {
  conversationId: string;
  toolUseId: string;
  approved?: boolean;
  resultJson?: string;
  errorCode?: string;
  locale?: string;
  /** P5: "studio:website|email|document" when chatting from the AI studio. */
  surface?: string;
  /** P7: pane candidate — must ride the re-entry too (same ⚠M1 reasoning). */
  paneCandidateId?: string;
  /**
   * P6: inspiration-image ids — the re-entry MUST carry them too (plan ⚠M1):
   * a generate_* can fire inside the RESUMED turn, where the original chat
   * POST's ids would otherwise be lost.
   */
  inspoImageIds?: string[];
};

/**
 * Discriminated union — event pages chat against the event-scoped endpoint
 * (full event context, ai:use cap), the dashboard against the company-scoped
 * one (events overview, company role gate). The explicit `kind` tag is the
 * single discriminant everywhere (URL pick, provider scope key) so a future
 * third scope can't be added to one site and missed in another.
 */
export type AiChatScope =
  | { kind: "event"; id: string }
  | { kind: "company"; id: string };

function chatUrl(scope: AiChatScope): string {
  // Company path is /{companyId}/company-chat (id directly after the
  // resource segment — the backend auth filter reads it positionally).
  return scope.kind === "event"
    ? `${API_BASE_URL}/api/v1/ai-assistant/${scope.id}/chat`
    : `${API_BASE_URL}/api/v1/ai-assistant/${scope.id}/company-chat`;
}

async function openStream(
  url: string,
  body: unknown,
  signal: AbortSignal,
): Promise<Response> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
  };
  const csrf = readCookie(CSRF_COOKIE);
  if (csrf) headers["X-CSRF-Token"] = csrf;

  return fetch(url, {
    method: "POST",
    credentials: "include",
    headers,
    body: JSON.stringify(body),
    signal,
  });
}

/**
 * Streams one chat turn. Calls `onEvent` for every SSE frame (including
 * pings — callers ignore what they don't need). Resolves when the stream
 * ends; throws with the backend errorCode (i18n-key convention) on a
 * non-OK response, or rethrows abort/network errors.
 */
export async function streamAiChat(
  scope: AiChatScope,
  body: AiChatBody,
  onEvent: (event: AiStreamEvent) => void,
  signal: AbortSignal,
): Promise<void> {
  return streamSse(chatUrl(scope), body, onEvent, signal);
}

/**
 * Resumes a turn paused on a tool call (confirmation gate or client tool).
 * Both scopes are supported: the event chat exposes the full tool set, while
 * the company/dashboard chat exposes only the universal support tools
 * (report_bug / escalate_to_human) — both can pause and must be resumable, or
 * the turn is left with an unanswered tool_use (a bricked conversation).
 */
export async function streamAiToolResult(
  scope: AiChatScope,
  body: AiToolResultBody,
  onEvent: (event: AiStreamEvent) => void,
  signal: AbortSignal,
): Promise<void> {
  const url =
    scope.kind === "event"
      ? `${API_BASE_URL}/api/v1/ai-assistant/${scope.id}/chat/tool-result`
      : `${API_BASE_URL}/api/v1/ai-assistant/${scope.id}/company-chat/tool-result`;
  return streamSse(url, body, onEvent, signal);
}

export type AiSuggestBody = {
  conversationId: string;
  /** UI locale (e.g. "de-DE") — suggestions are written in this language. */
  locale?: string;
  /** Builder surface the chat is driven from (null = drawer). */
  surface?: string;
};

/**
 * Fetches best-effort follow-up suggestions for a just-ended conversation —
 * a SEPARATE, non-streaming JSON call (not part of the SSE turn) so the chat
 * composer is never held open while the model thinks. Best-effort: callers
 * treat any failure / empty result as "no chips". Aborts with the turn.
 */
export async function fetchAiSuggestions(
  scope: AiChatScope,
  body: AiSuggestBody,
  signal: AbortSignal,
): Promise<string[]> {
  const url =
    scope.kind === "event"
      ? `/api/v1/ai-assistant/${scope.id}/chat/suggestions`
      : `/api/v1/ai-assistant/${scope.id}/company-chat/suggestions`;
  const res = await api.post<{ suggestions?: string[] }>(url, body, { signal });
  return res.data?.suggestions ?? [];
}

/** Mirror of backend AiGoalSuggestionsResponse — both parts may be empty. */
export type AiGoalSuggestions = {
  /** Tailored goal statement for the empty Eventziel field. */
  goal: string;
  /** Up to 3 alternative example statements for the example chips. */
  examples: string[];
  /**
   * KPI metrics that fit THIS goal, best first — the wizard's KPI step leads
   * with them. Optional: a backend that does not rank yet simply omits it and
   * the step falls back to the canonical order. Unknown names are dropped.
   */
  suggestedMetrics: KpiMetric[];
};

/**
 * One-shot, conversation-free goal suggestion for the goals setup wizard —
 * a tailored Eventziel prefill + example chips generated from the stored
 * event context (incl. the create-event interview's GOALS brief). Best-effort:
 * callers treat any failure / empty result as "no suggestion".
 */
export async function fetchAiGoalSuggestions(
  eventId: string,
  locale?: string,
  signal?: AbortSignal,
): Promise<AiGoalSuggestions> {
  // Through the shim's rethrow like every other one-shot endpoint: the raw
  // axios error carried "Request failed with status code 429" on `.message`,
  // so no caller could translate the code or read `retryAfter`
  // (audit 2026-09-28, M1). The streaming calls above stay raw on purpose.
  let data: Partial<AiGoalSuggestions> | undefined;
  try {
    const res = await api.post<Partial<AiGoalSuggestions>>(
      `/api/v1/ai-assistant/${eventId}/goal-suggestions`,
      { locale },
      { signal },
    );
    data = res.data;
  } catch (e) {
    rethrowApi(e);
  }
  // Defensive normalization of model output: trimmed, non-empty, de-duplicated,
  // capped — a duplicate/blank example would render broken chips (dup React
  // keys, pills that clear the field) and suppress the static fallback.
  const goal = (data?.goal ?? "").trim();
  const examples: string[] = [];
  for (const raw of data?.examples ?? []) {
    const value = typeof raw === "string" ? raw.trim() : "";
    if (!value || value === goal || examples.includes(value)) continue;
    examples.push(value);
    if (examples.length >= 3) break;
  }
  const suggestedMetrics: KpiMetric[] = [];
  const rawMetrics = (data as { suggestedMetrics?: unknown } | undefined)?.suggestedMetrics;
  for (const raw of Array.isArray(rawMetrics) ? rawMetrics : []) {
    // COHORT_SHARE needs a facet + value — it cannot be a one-click chip suggestion.
    if (typeof raw !== "string" || raw === "COHORT_SHARE") continue;
    if (!(KPI_METRICS as string[]).includes(raw) || suggestedMetrics.includes(raw as KpiMetric)) continue;
    suggestedMetrics.push(raw as KpiMetric);
    if (suggestedMetrics.length >= 4) break;
  }
  return { goal, examples, suggestedMetrics };
}

/** One proposed cohort target, already coerced into a savable row. */
export type AiGoalPlanTarget = {
  /** Exactly what a save would write — label, facet, percent. */
  target: CohortTarget;
  /**
   * The SERVER's verdict: `true` — the label is a stored (or declared) value,
   * so the analysis will COUNT this target instead of estimating it; `false`
   * — no stored value matches, the analysis estimates; `null` — the value
   * list was truncated and the label could not be checked either way. JSON-B
   * OMITS a null on the wire, so the gap is mapped here, not read as `false`
   * (audit 2026-09-28, B4).
   */
  countable: boolean | null;
  /**
   * Guests carrying the value today; absent when not countable. 0 is a real
   * answer (a declared value nobody carries yet — B1).
   */
  matchedNow?: number;
  /** One line of reasoning — why this cohort, or what is missing for it. */
  rationale: string;
};

/** One proposed KPI, already complete enough to save (threshold + direction). */
export type AiGoalPlanKpi = {
  kpi: EventGoalKpi;
  rationale: string;
};

/**
 * One of Velo's questions back — asked only when the organiser's words leave
 * open something that would change the plan (the main audience, the size, the
 * outcome), with 2-4 short answers to click (goal clarify 2026-10-04).
 */
export type AiGoalPlanQuestion = {
  question: string;
  options: string[];
  /** Several options may be picked together. */
  multiSelect: boolean;
};

/**
 * What the event is FOR, measured after it (outcome measures, 2026-10-05): a
 * yes/no guest field the organiser fills in afterwards ("Probefahrt gebucht")
 * and the share of the whole list that should reach the "yes". `field` is the
 * guest-field KEY the backend made safe for the field API; `existing` = the
 * list already has that field, nothing to create.
 */
export type AiGoalPlanOutcome = {
  field: string;
  label: string;
  yesValue: string;
  /** The second option of a NEW field; empty for an existing one. */
  noValue: string;
  targetPercent: number;
  rationale: string;
  existing: boolean;
  /**
   * The group the share is taken WITHIN (W3) — "test drive booked among the
   * prospects"; the server keeps it only for a value the list stores.
   * Absent = a share of the whole list.
   */
  group?: { dimension: string; value: string };
};

/** The backend's caps on an outcome — the client's own guard mirrors them. */
const MAX_PLAN_OUTCOMES = 3;
/** The additional-field key rule (AdditionalFieldConstraints.KEY_PATTERN). */
const OUTCOME_KEY = /^[A-Za-z0-9_-]{1,100}$/;

/** An answer the organiser gave to one of those questions, sent back verbatim. */
export type AiGoalPlanAnswer = { question: string; answer: string };

/** What the parser keeps of a question — the backend's own reading caps. */
const MAX_PLAN_QUESTIONS = 3;
const MAX_PLAN_QUESTION_TEXT = 160;
const MAX_PLAN_OPTION_TEXT = 60;
const MAX_PLAN_OPTIONS = 4;

/**
 * A row of the model's plan the SERVER left out because it broke a rule
 * (goals "abstract" wave W4, 2026-10-05): `part` = target / kpi / outcome,
 * `reason` = a code (`againstRole`, `overHundred`, `notStoredValue`, …) the
 * sheet words. Nothing is invented in its place — the row is simply gone.
 */
export type AiGoalPlanDropped = { part: "target" | "kpi" | "outcome"; label: string; reason: string };

/** Mirror of backend AiGoalPlanResponse, rows coerced. Every part may be empty. */
export type AiGoalPlan = {
  /** The measurable goal statement. */
  goal: string;
  /**
   * Why THIS mix for this kind of event — the model's whole-plan reasoning,
   * a few sentences in the locale. Absent when the server sent none
   * (older backends, an empty plan). Display only, never saved (B6).
   */
  summary?: string;
  targets: AiGoalPlanTarget[];
  kpis: AiGoalPlanKpi[];
  /** What the percentages are relative to. */
  basis: {
    /** Active guests; 0 when the plan is not grounded. */
    roster: number;
    expectedCapacity?: number;
    /** false = the caller may not read guests: nothing was verified. */
    grounded: boolean;
  };
  /**
   * Velo's questions back, when the intent was too vague to plan well —
   * absent when there are none (older backends never send any). The plan
   * above is still the best guess; answering only sharpens it.
   */
  questions?: AiGoalPlanQuestion[];
  /** After-event success measures — absent when there are none. */
  outcomes?: AiGoalPlanOutcome[];
  /** What kind of goal the server read (ACQUISITION, RETENTION, …) — absent from older backends. */
  intentKind?: string;
  /** Rows the server left out, with the reason — absent when none were. */
  dropped?: AiGoalPlanDropped[];
  /** Hints about the plan as a whole (`missingOutcome`) — absent when there are none. */
  notes?: string[];
  /**
   * Set only when the plan is empty because something BROKE (`aiTimeout`,
   * `aiProviderError`, `aiMalformedOutput`, `aiQuotaExceeded`, `aiDisabled`).
   * The endpoint answers 200 either way, so without this the UI told the user
   * to rewrite their intent after an outage (audit 2026-09-22).
   */
  errorCode?: string;
};

const EMPTY_GOAL_PLAN: AiGoalPlan = {
  goal: "",
  targets: [],
  kpis: [],
  basis: { roster: 0, grounded: false },
};

/**
 * One-shot goal PLAN: the organiser's intent ("Produktlaunch Dyson Airblade")
 * becomes a measurable goal — statement, cohort targets over the roster's
 * STORED values, KPIs with numbers — that the user reviews before anything is
 * written. The endpoint persists nothing; the client saves what survives the
 * review through the ordinary goal PUT.
 *
 * Model output stays untrusted on this side too: every row runs through the
 * same `coerceCohortTarget` / `coerceKpi` the Velo drawer uses, so a row that
 * could not be saved never reaches the review sheet. A failed request is a
 * thrown error (the caller shows it); a successful but empty plan is the
 * backend's own "no suggestion".
 */
export async function fetchAiGoalPlan(
  eventId: string,
  body: { intent?: string; locale?: string; answers?: AiGoalPlanAnswer[] },
  signal?: AbortSignal,
): Promise<AiGoalPlan> {
  type PlanWire = {
    goal?: string;
    summary?: unknown;
    targets?: unknown[];
    kpis?: unknown[];
    basis?: { roster?: number; expectedCapacity?: number; grounded?: boolean };
    questions?: unknown;
    outcomes?: unknown;
    intentKind?: unknown;
    dropped?: unknown;
    notes?: unknown;
    errorCode?: unknown;
  };
  // `answers` only travels when there are some — an older backend reads the
  // body it always read, and a first run looks exactly like before.
  const { answers, ...rest } = body;
  const payload = answers && answers.length > 0 ? { ...rest, answers } : rest;
  // Same rethrow as the suggestions call: a 429 on the AI rate-limit bucket
  // reaches the sheet as `message: "rateLimited"` + `status` + `retryAfter`,
  // which is what its countdown needs (audit 2026-09-28, M1).
  let data: PlanWire | undefined;
  try {
    const res = await api.post<PlanWire>(`/api/v1/ai-assistant/${eventId}/goal-plan`, payload, { signal });
    data = res.data;
  } catch (e) {
    rethrowApi(e);
  }
  if (!data) return EMPTY_GOAL_PLAN;

  const targets: AiGoalPlanTarget[] = [];
  for (const raw of Array.isArray(data.targets) ? data.targets : []) {
    const row = raw as { countable?: unknown; matchedNow?: unknown; rationale?: unknown };
    const target = coerceCohortTarget(row as Parameters<typeof coerceCohortTarget>[0]);
    if (!target) continue;
    // Two rows for one (facet, value) would fight over one number — the
    // backend dedupes already, this is the client's own guard.
    if (targets.some((t) => t.target.label === target.label && t.target.dimension === target.dimension)) {
      continue;
    }
    targets.push({
      target,
      // Three states, strictly: anything that is not a boolean is "unchecked".
      countable: row.countable === true ? true : row.countable === false ? false : null,
      matchedNow: typeof row.matchedNow === "number" ? row.matchedNow : undefined,
      rationale: typeof row.rationale === "string" ? row.rationale.trim() : "",
    });
    if (targets.length >= MAX_COHORT_TARGETS) break;
  }

  const kpis: AiGoalPlanKpi[] = [];
  for (const raw of Array.isArray(data.kpis) ? data.kpis : []) {
    const row = raw as { rationale?: unknown };
    const kpi = coerceKpi(raw as Parameters<typeof coerceKpi>[0]);
    if (!kpi) continue;
    if (kpis.some((k) => sameKpiIdentity(k.kpi, kpi))) continue;
    kpis.push({ kpi, rationale: typeof row.rationale === "string" ? row.rationale.trim() : "" });
    if (kpis.length >= MAX_KPIS) break;
  }

  const roster = typeof data.basis?.roster === "number" && data.basis.roster > 0 ? data.basis.roster : 0;
  const capacity = typeof data.basis?.expectedCapacity === "number" && data.basis.expectedCapacity > 0
    ? data.basis.expectedCapacity
    : undefined;
  const questions = coerceGoalPlanQuestions(data.questions);
  const outcomes = coerceGoalPlanOutcomes(data.outcomes);
  const dropped = coerceGoalPlanDropped(data.dropped);
  const notes = Array.isArray(data.notes)
    ? data.notes.filter((n): n is string => typeof n === "string" && /^[A-Za-z]{1,40}$/.test(n)).slice(0, 5)
    : [];
  const intentKind = typeof data.intentKind === "string" && /^[A-Z_]{1,40}$/.test(data.intentKind)
    ? data.intentKind
    : undefined;
  return {
    // typeof, not `?? ""`: a non-string `goal` (a number, an object) would
    // throw on .trim() and turn a best-effort endpoint into a crash.
    goal: typeof data.goal === "string" ? data.goal.trim().slice(0, MAX_GOAL_TEXT_LENGTH) : "",
    ...(typeof data.summary === "string" && data.summary.trim() ? { summary: data.summary.trim().slice(0, 400) } : {}),
    targets,
    kpis,
    basis: { roster, expectedCapacity: capacity, grounded: data.basis?.grounded === true },
    ...(questions.length > 0 ? { questions } : {}),
    ...(outcomes.length > 0 ? { outcomes } : {}),
    ...(intentKind ? { intentKind } : {}),
    ...(dropped.length > 0 ? { dropped } : {}),
    ...(notes.length > 0 ? { notes } : {}),
    ...(typeof data.errorCode === "string" && data.errorCode ? { errorCode: data.errorCode } : {}),
  };
}

/**
 * Velo's questions, held to the backend's own caps: a question without at
 * least two distinct answers to click is not a question the UI can ask, and
 * model output stays untrusted on this side too.
 */
function coerceGoalPlanQuestions(raw: unknown): AiGoalPlanQuestion[] {
  const out: AiGoalPlanQuestion[] = [];
  for (const item of Array.isArray(raw) ? raw : []) {
    if (!item || typeof item !== "object") continue;
    const row = item as { question?: unknown; options?: unknown; multiSelect?: unknown };
    const question = typeof row.question === "string" ? row.question.trim().slice(0, MAX_PLAN_QUESTION_TEXT) : "";
    if (!question) continue;
    const options: string[] = [];
    for (const option of Array.isArray(row.options) ? row.options : []) {
      const value = typeof option === "string" ? option.trim().slice(0, MAX_PLAN_OPTION_TEXT) : "";
      if (!value || options.some((o) => o.toLocaleLowerCase() === value.toLocaleLowerCase())) continue;
      options.push(value);
      if (options.length >= MAX_PLAN_OPTIONS) break;
    }
    if (options.length < 2) continue;
    out.push({ question, options, multiSelect: row.multiSelect === true });
    if (out.length >= MAX_PLAN_QUESTIONS) break;
  }
  return out;
}

/**
 * The outcomes, held to the backend's rules: a key the field API accepts, a
 * "yes" to count, a share in (0, 100], and — for a field still to be created —
 * a second answer to be its other option.
 */
function coerceGoalPlanOutcomes(raw: unknown): AiGoalPlanOutcome[] {
  const out: AiGoalPlanOutcome[] = [];
  for (const item of Array.isArray(raw) ? raw : []) {
    if (!item || typeof item !== "object") continue;
    const row = item as Record<string, unknown>;
    const text = (v: unknown, max: number) => (typeof v === "string" ? v.trim().slice(0, max) : "");
    const field = text(row.field, 100);
    const label = text(row.label, 60) || field;
    const yesValue = text(row.yesValue, MAX_PLAN_OPTION_TEXT);
    const noValue = text(row.noValue, MAX_PLAN_OPTION_TEXT);
    const existing = row.existing === true;
    const target = typeof row.targetPercent === "number" ? Math.round(row.targetPercent * 10) / 10 : NaN;
    if (!OUTCOME_KEY.test(field) || !yesValue || !(target > 0 && target <= 100)) continue;
    if (!existing && (!noValue || noValue.toLocaleLowerCase() === yesValue.toLocaleLowerCase())) continue;
    if (out.some((o) => o.field.toLowerCase() === field.toLowerCase())) continue;
    const groupDimension = text(row.groupDimension, 200);
    const groupValue = text(row.groupLabel, 120);
    out.push({
      field,
      label,
      yesValue,
      noValue: existing ? "" : noValue,
      targetPercent: target,
      rationale: text(row.rationale, 240),
      existing,
      ...(groupDimension && groupValue && groupDimension !== `additionalFields.${field}`
        ? { group: { dimension: groupDimension, value: groupValue } }
        : {}),
    });
    if (out.length >= MAX_PLAN_OUTCOMES) break;
  }
  return out;
}

/** The server's dropped rows — display only, so a bad row is skipped, never thrown on. */
function coerceGoalPlanDropped(raw: unknown): AiGoalPlanDropped[] {
  const out: AiGoalPlanDropped[] = [];
  for (const item of Array.isArray(raw) ? raw : []) {
    if (!item || typeof item !== "object") continue;
    const row = item as Record<string, unknown>;
    const part = row.part === "target" || row.part === "kpi" || row.part === "outcome" ? row.part : null;
    const reason = typeof row.reason === "string" && /^[A-Za-z]{1,40}$/.test(row.reason) ? row.reason : null;
    if (!part || !reason) continue;
    const label = typeof row.label === "string" ? row.label.trim().slice(0, 80) : "";
    out.push({ part, label, reason });
    if (out.length >= 20) break;
  }
  return out;
}

/** One row in the chat-history list (mirrors backend AiConversationSummary). */
export type AiConversationSummary = {
  conversationId: string;
  /** First user message, truncated; null for an image-only first turn. */
  title: string | null;
  /** ISO-8601 last-activity time. */
  updatedAt: string;
};

/** A restored transcript turn (mirrors backend AiConversationMessage). */
export type AiConversationMessageDto = {
  role: "user" | "assistant";
  content: string;
  /** assistant only — credits the user's MESSAGE had cost through this step;
   *  absent when nothing was billed. Lets a reopened conversation show the
   *  price again, which the live SSE frame cannot after the stream closed.
   *  Named for the message: the field it replaced shipped under the name
   *  `creditCost` meaning a per-CALL delta, and is deliberately not read. */
  messageCredits?: number;
};

/** Most-recent-first list of the user's past conversations for this scope. */
export async function listAiConversations(
  scope: AiChatScope,
): Promise<AiConversationSummary[]> {
  const url =
    scope.kind === "event"
      ? `/api/v1/ai-assistant/${scope.id}/conversations`
      : `/api/v1/ai-assistant/${scope.id}/company-conversations`;
  const res = await api.get<{ conversations?: AiConversationSummary[] }>(url);
  return res.data?.conversations ?? [];
}

/** One support request in the history (mirrors backend SupportRequestSummary). */
export type SupportRequestSummary = {
  id: string;
  type: "bug" | "escalation";
  message: string;
  /** Conversation it was filed from, or null. Used to jump back to that chat. */
  conversationId: string | null;
  /** ISO-8601 time it was filed. */
  createdAt: string;
};

/** This user's recent support requests (bug reports / escalations) for this scope. */
export async function listAiSupportRequests(
  scope: AiChatScope,
): Promise<SupportRequestSummary[]> {
  const url =
    scope.kind === "event"
      ? `/api/v1/ai-assistant/${scope.id}/support-requests`
      : `/api/v1/ai-assistant/${scope.id}/company-support-requests`;
  const res = await api.get<{ requests?: SupportRequestSummary[] }>(url);
  return res.data?.requests ?? [];
}

/** Full text transcript of one conversation (oldest-first) to reopen + continue it. */
export async function getAiConversation(
  scope: AiChatScope,
  conversationId: string,
): Promise<AiConversationMessageDto[]> {
  const url =
    scope.kind === "event"
      ? `/api/v1/ai-assistant/${scope.id}/conversation/${conversationId}`
      : `/api/v1/ai-assistant/${scope.id}/company-conversation/${conversationId}`;
  const res = await api.get<{ messages?: AiConversationMessageDto[] }>(url);
  return res.data?.messages ?? [];
}

/**
 * Deletes one of the user's own conversations (history-menu trash button).
 * Server-side idempotent: already-gone is a 204 too, so a double-fire is safe.
 */
export async function deleteAiConversation(
  scope: AiChatScope,
  conversationId: string,
): Promise<void> {
  const url =
    scope.kind === "event"
      ? `/api/v1/ai-assistant/${scope.id}/conversation/${conversationId}`
      : `/api/v1/ai-assistant/${scope.id}/company-conversation/${conversationId}`;
  await api.delete(url);
}

/** Interview briefs pinned on a wizard conversation (see getAiConversationBrief). */
export interface AiConversationBriefs {
  /** Overall durable brief (set_event_brief), or null. */
  brief: string | null;
  /** Per-setup-section taste briefs (set_section_brief, key = SetupSectionKey name). */
  sectionBriefs: Record<string, string> | null;
  /** Interview-chosen setup sections in priority order (set_setup_sections), or null. */
  sectionPlan: string[] | null;
}

/**
 * The durable interview briefs pinned on one conversation (set_event_brief /
 * set_section_brief). Used once by the create-event wizard at Create time:
 * the overall brief becomes the new event's Velo handoff, the section briefs
 * seed the setup plan.
 */
export async function getAiConversationBrief(
  scope: AiChatScope,
  conversationId: string,
): Promise<AiConversationBriefs> {
  const url =
    scope.kind === "event"
      ? `/api/v1/ai-assistant/${scope.id}/conversation/${conversationId}`
      : `/api/v1/ai-assistant/${scope.id}/company-conversation/${conversationId}`;
  const res = await api.get<{
    brief?: string | null;
    sectionBriefs?: Record<string, string> | null;
    sectionPlan?: string[] | null;
  }>(url);
  return {
    brief: res.data?.brief ?? null,
    sectionBriefs: res.data?.sectionBriefs ?? null,
    sectionPlan: res.data?.sectionPlan ?? null,
  };
}

async function streamSse(
  url: string,
  body: unknown,
  onEvent: (event: AiStreamEvent) => void,
  signal: AbortSignal,
): Promise<void> {
  let res = await openStream(url, body, signal);

  if (res.status === 401) {
    // "unauthorized" is reserved for a reissue the server REFUSED: the
    // provider answers that code with `veltric:auth-lost`. A reissue with no
    // answer at all is a network verdict — red dot, and "network" is not a
    // known errorCode, so the provider shows the generic retryable chat
    // error, the same COPY a failed stream fetch gets. (The stream's own raw
    // `fetch` casts no dot verdict either way — raw fetch is a documented
    // blind spot of the dot; only the shared reissue is judged here.)
    const outcome = await refreshAccessTokenOutcome().catch(() => "rejected" as const);
    if (outcome === "unreachable") {
      useConnectionStore.getState().noteNetworkFailure();
      throw new Error("network");
    }
    if (outcome !== "ok") throw new Error("unauthorized");
    res = await openStream(url, body, signal);
  }

  if (!res.ok || !res.body) {
    let code = "serverError";
    try {
      // Backend error envelope = ErrorFormater.ErrorResponse {errorCode, message}.
      const payload = (await res.json()) as { errorCode?: string };
      if (payload?.errorCode) code = payload.errorCode;
    } catch {
      // non-JSON error body — keep the generic code
    }
    throw new Error(code);
  }

  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = "";
  const emitFrame = (frame: string) => {
    for (const line of frame.split("\n")) {
      if (!line.startsWith("data:")) continue; // comments / event: lines
      const data = line.substring(5).trim();
      if (!data) continue;
      try {
        onEvent(JSON.parse(data) as AiStreamEvent);
      } catch {
        // malformed frame — skip rather than kill the stream
      }
    }
  };
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      // Normalize CRLF up front: a proxy switching the stream to \r\n line
      // endings must not stop frames from delimiting. A \r\n pair split
      // across two chunks is healed by re-scanning the carried-over buffer.
      buffer = (buffer + value).replace(/\r\n/g, "\n");
      // SSE frames are separated by a blank line.
      for (;;) {
        const sep = buffer.indexOf("\n\n");
        if (sep === -1) break;
        const frame = buffer.substring(0, sep);
        buffer = buffer.substring(sep + 2);
        emitFrame(frame);
      }
    }
    // Flush the tail: a final frame the server didn't terminate with a blank
    // line before closing the stream would otherwise be dropped silently.
    if (buffer) emitFrame(buffer);
  } finally {
    reader.releaseLock();
  }
}


